import { useQuery } from 'react-query';

export interface HeaderMenuOption {
  value: string;
  label: string;
  path: string;
}

interface NavigationSubMenu {
  url?: string;
  title?: string;
}

interface NavigationTranslation {
  languages_code?: string;
  language_code?: string;
  locale?: string;
  title?: string;
  sub_menus?: NavigationSubMenu[];
}

interface NavigationItem {
  id?: string | number;
  navigation_url?: string;
  translations?: NavigationTranslation[];
}

const getLocaleCandidates = (locale?: string) => {
  if (!locale) return [];
  if (locale.toLowerCase() === 'vi') return ['vi', 'vn', 'VI', 'VN'];
  return [locale, locale.toLowerCase(), locale.toUpperCase()];
};

const findMatchedTranslation = (
  translations: NavigationTranslation[],
  locale?: string,
) => {
  const localeCandidates = getLocaleCandidates(locale);
  return translations.find((translation: NavigationTranslation) => {
    const language = translation?.languages_code ?? translation?.language_code ?? translation?.locale;
    return typeof language === 'string' && localeCandidates.includes(language);
  });
};

const normalizePath = (path: string) => {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  return path.startsWith('/') ? path : `/${path}`;
};

const buildMenuPath = (navigationUrl?: string, subMenuUrl?: string) => {
  if (!subMenuUrl) return '';

  if (subMenuUrl.startsWith('http://') || subMenuUrl.startsWith('https://')) {
    return subMenuUrl;
  }

  const child = subMenuUrl.trim().replace(/\s+/g, '-');

  return normalizePath(`/${child}`.replace(/\/{2,}/g, '/'));
};

const normalizeHeaderMenuOptions = (content: NavigationItem[], locale?: string): HeaderMenuOption[] => {
  if (!Array.isArray(content)) {
    return [];
  }

  return content.flatMap((item: NavigationItem) => {
    const translations = Array.isArray(item?.translations) ? item.translations : [];
    if (!translations.length) return [];

    const matchedTranslation =
      findMatchedTranslation(translations, locale) ?? translations[0];
    const subMenus = Array.isArray(matchedTranslation?.sub_menus)
      ? matchedTranslation.sub_menus
      : [];

    return subMenus
      .map((subMenu: NavigationSubMenu, index: number) => {
        const path = buildMenuPath(item?.navigation_url, subMenu?.url);
        const value = `${item?.id ?? item?.navigation_url ?? 'menu'}-${index}`;
        const label = (subMenu?.title ?? '').trim();

        return {
          value: String(value),
          label,
          path,
        };
      })
      .filter(option => option.label && option.path);
  });
};

export default function useHeaderMenuOptions(locale?: string) {
  return useQuery(
    ['header-menu-options', locale],
    async () => {
      const response = await fetch('/internal-api/directus/navigation');
      if (!response.ok) {
        throw new Error('Failed to fetch navigation options');
      }
      const payload = await response.json();
      const content = Array.isArray(payload?.data) ? payload.data : [];
      return normalizeHeaderMenuOptions(content, locale);
    },
    {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  );
}
